import { isValidZone, MS_MIN, zoneCity, zoneOffsetMs } from '@/core/time';
import type { SleepSession } from '@/core/types';
import type { WorldCache } from '@/modules/activity/trace';

// Which clock each night was slept on, and what travel did to it.
//
// Oura stores every sleep with the UTC offset the phone had when it synced. That's wrong often
// enough to matter: a ring that synced before the phone caught up with a new zone files a
// Tokyo night under the old zone, and some nights arrive as plain UTC. So Oura's offset is
// never trusted on its own:
//
//   1. where you said you were that day (Settings-free: the ! on a night);
//   2. else where Trace saw you within ~20 hours before bedtime / after waking;
//   3. else Trips App: a hotel booked over the night, or a flight / booking within ~20 hours;
//   4. else home, if you have one and Oura's offset doesn't say otherwise;
//   5. else unknown: the night gets a ! and asks where you were. Until you answer it's drawn on
//      Oura's clock (else this device's zone), as a placeholder.
//
// Durations never depend on any of this: they're real elapsed time. What the zone changes is
// the clock times shown (23:10–07:05 as it was on the wall), which day a night belongs to, and
// the travel story: how far from home the clock was (when you have one), how much the clock jumped since the last
// night (and so how long the day in between really was), clocks that moved during the sleep
// itself (overnight flights), and how much jet lag the body is probably still carrying.

const NEAR_MS = 20 * 3_600_000;
const DAY_MS = 86_400_000;

export interface ZonePoint {
  /** epoch ms */
  at: number;
  tz: string;
}

/** Every moment Trace knows the zone of (trip starts and ends), oldest first. */
export function tracePoints(cache: WorldCache | null): ZonePoint[] {
  if (!cache) return [];
  const out: ZonePoint[] = [];
  for (const t of Object.values(cache.tracks)) {
    if (!t.tz || !isValidZone(t.tz)) continue;
    out.push({ at: t.start, tz: t.tz }, { at: t.end, tz: t.tz });
  }
  return out.sort((a, b) => a.at - b.at);
}

/** Last point at or before `ms` and first point at or after it (binary search). */
function around(points: ZonePoint[], ms: number): [ZonePoint | undefined, ZonePoint | undefined] {
  let lo = 0;
  let hi = points.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid]!.at <= ms) lo = mid + 1;
    else hi = mid;
  }
  return [points[lo - 1], points[lo]];
}

export type ClockSource = 'manual' | 'trace' | 'trips' | 'home' | 'unknown';

export interface NightClock {
  /** the zone the night is labeled with (where you woke up), or null when it isn't known */
  zone: string | null;
  /** minutes east of UTC on the wall clock at bedtime / at wake-up */
  startOff: number;
  endOff: number;
  source: ClockSource;
  /** "Tokyo", or for an unknown night Oura's guess ("UTC+9?") */
  place: string;
}

/** A Trips App booking, as far as zones go (protocol TripsSpan). */
export interface ZoneSpan {
  start: number;
  end: number;
  tz: string;
  endTz: string;
  type: string;
}

const offMin = (tz: string, ms: number) => Math.round(zoneOffsetMs(tz, ms) / MS_MIN);

/** "UTC+9", "UTC−3:30", "UTC" */
export function offsetLabel(min: number): string {
  if (!min) return 'UTC';
  const a = Math.abs(min);
  return `UTC${min < 0 ? '−' : '+'}${Math.floor(a / 60)}${a % 60 ? `:${String(a % 60).padStart(2, '0')}` : ''}`;
}

/**
 * The zone Trips App puts you in at `ms`: inside a booking (a hotel stay, or a flight: its
 * nearer end), else the closest booking start/end within `near` ms. Spans sorted by start.
 */
export function tripsZoneAt(spans: ZoneSpan[], ms: number, near = NEAR_MS): string | undefined {
  let best: { d: number; tz: string } | undefined;
  for (const sp of spans) {
    if (sp.start - ms > near) break;
    if (sp.start <= ms && ms <= sp.end) {
      // Hotels beat anything else; inside a flight, the end you're closer to.
      const tz = ms - sp.start < sp.end - ms ? sp.tz : sp.endTz;
      if (sp.type === 'lodging') return tz;
      best = { d: 0, tz };
      continue;
    }
    for (const [at, tz] of [
      [sp.start, sp.tz],
      [sp.end, sp.endTz],
    ] as const) {
      const d = Math.abs(at - ms);
      if (d <= near && (!best || d < best.d)) best = { d, tz };
    }
  }
  return best?.tz;
}

export interface ZoneSources {
  /** Trace moments with zones, oldest first */
  trace: ZonePoint[];
  /** Trips App bookings, by start */
  trips: ZoneSpan[];
  /** the zone you said you were in on this night's day, if you did */
  told?: string;
  /** your home zone: nights nothing else places are put there unless Oura's offset disagrees */
  home?: string | null;
}

export function nightClock(
  s: Pick<SleepSession, 'start' | 'end' | 'startOffset' | 'endOffset' | 'zone'>,
  src: ZoneSources,
  fallback: string,
): NightClock {
  const told = isValidZone(s.zone) ? s.zone : isValidZone(src.told) ? src.told : undefined;
  if (told) {
    return {
      zone: told,
      startOff: offMin(told, s.start),
      endOff: offMin(told, s.end),
      source: 'manual',
      place: zoneCity(told),
    };
  }
  const [before] = around(src.trace, s.start);
  const [, after] = around(src.trace, s.end);
  const tb = before && s.start - before.at <= NEAR_MS ? before.tz : undefined;
  const ta = after && after.at - s.end <= NEAR_MS ? after.tz : undefined;
  if (tb || ta) {
    // One side from Trace, the other filled by Trips App when it knows better than "same".
    const startZone = tb ?? tripsZoneAt(src.trips, s.start) ?? ta!;
    const endZone = ta ?? tripsZoneAt(src.trips, s.end) ?? tb!;
    return {
      zone: endZone,
      startOff: offMin(startZone, s.start),
      endOff: offMin(endZone, s.end),
      source: 'trace',
      place: zoneCity(endZone),
    };
  }
  const pb = tripsZoneAt(src.trips, s.start);
  const pa = tripsZoneAt(src.trips, s.end);
  if (pb || pa) {
    const startZone = (pb ?? pa)!;
    const endZone = (pa ?? pb)!;
    return {
      zone: endZone,
      startOff: offMin(startZone, s.start),
      endOff: offMin(endZone, s.end),
      source: 'trips',
      place: zoneCity(endZone),
    };
  }
  // Nothing places it: home, unless Oura's clock says you were somewhere else.
  const home = src.home;
  if (
    home &&
    isValidZone(home) &&
    (s.startOffset === undefined || s.startOffset === offMin(home, s.start)) &&
    (s.endOffset === undefined || s.endOffset === offMin(home, s.end))
  ) {
    return {
      zone: home,
      startOff: offMin(home, s.start),
      endOff: offMin(home, s.end),
      source: 'home',
      place: zoneCity(home),
    };
  }
  // Unknown: draw it on Oura's clock (else the fallback) until you say where you were.
  if (s.startOffset !== undefined || s.endOffset !== undefined) {
    const startOff = s.startOffset ?? s.endOffset!;
    const endOff = s.endOffset ?? startOff;
    return { zone: null, startOff, endOff, source: 'unknown', place: `${offsetLabel(endOff)}?` };
  }
  return {
    zone: null,
    startOff: offMin(fallback, s.start),
    endOff: offMin(fallback, s.end),
    source: 'unknown',
    place: `${zoneCity(fallback)}?`,
  };
}

/** "YYYY-MM-DD" on a wall clock `off` minutes east of UTC. */
export function dayAt(ms: number, off: number): string {
  return new Date(ms + off * MS_MIN).toISOString().slice(0, 10);
}

/** Minutes after local midnight (0–1439) on a clock `off` minutes east of UTC. */
export function minuteOfDay(ms: number, off: number): number {
  const m = Math.floor((ms + off * MS_MIN) / MS_MIN) % 1440;
  return m < 0 ? m + 1440 : m;
}

/** Bedtime as minutes from midnight, evenings negative: 23:30 → −30, 01:15 → 75. */
export function bedMinute(ms: number, off: number): number {
  const m = minuteOfDay(ms, off);
  return m >= 720 ? m - 1440 : m;
}

/** A clock difference folded into (−12h, +12h]: +13h of travel is really 11h the other way. */
export function foldShift(min: number): number {
  let m = min % 1440;
  if (m > 720) m -= 1440;
  if (m <= -720) m += 1440;
  return m;
}

/** The zone you slept in most over these nights. */
export function usualZone(clocks: NightClock[], fallback: string): string {
  const count = new Map<string, number>();
  for (const c of clocks) if (c.zone) count.set(c.zone, (count.get(c.zone) ?? 0) + 1);
  let best = fallback;
  let n = 0;
  for (const [z, k] of count)
    if (k > n) {
      best = z;
      n = k;
    }
  return best;
}

// ------------------------------------------------------------------ the body clock
//
// After a zone change the body clock moves toward the new local clock by about an hour a day
// when it has to run earlier (flying east) and about an hour and a half a day when it can run
// later (flying west). "Jet lag" here is how far the body clock still is from the wall clock.

export const ADVANCE_PER_DAY_MIN = 60;
export const DELAY_PER_DAY_MIN = 90;

export interface BodyClockStep {
  /** minutes east of UTC the body is living on */
  bodyOff: number;
  /** wall clock minus body clock, folded: + = body behind (went east), − = body ahead (went west) */
  lagMin: number;
}

/**
 * Walks nights oldest → newest. `nights` give the wall-clock offset at bedtime and when it was.
 * Starts on `startOff` (home, or the first known night) and moves toward each night's clock at
 * the rates above.
 */
export function bodyClock(
  nights: { at: number; off: number }[],
  startOff: number,
): BodyClockStep[] {
  let body = startOff;
  let prevAt = nights[0]?.at ?? 0;
  return nights.map((n) => {
    const days = Math.max(0, (n.at - prevAt) / DAY_MS);
    prevAt = n.at;
    const diff = foldShift(n.off - body);
    const move =
      diff > 0
        ? Math.min(diff, ADVANCE_PER_DAY_MIN * days)
        : Math.max(diff, -DELAY_PER_DAY_MIN * days);
    body = body + move;
    return { bodyOff: body, lagMin: Math.round(foldShift(n.off - body)) };
  });
}

/** Days until a lag is gone at the usual rates. */
export function daysToAdjust(lagMin: number): number {
  if (Math.abs(lagMin) < 30) return 0;
  return Math.ceil(Math.abs(lagMin) / (lagMin > 0 ? ADVANCE_PER_DAY_MIN : DELAY_PER_DAY_MIN));
}
